import { Bell, LogOut, Menu, Wallet } from "lucide-react";
import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Card, CardContent } from "./ui/card";
import { SidebarTrigger } from "./ui/sidebar";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import ThemeToggle from "./ThemeToggle";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
  const { isDarkMode } = useTheme();
  const { hasRole, user, logout } = useAuth();
  const navigate = useNavigate();

  // Check if user is admin (not superadmin)
  const isAdmin = hasRole('admin') && !hasRole('super_admin') && !hasRole('superadmin');
  
  // Format role for display
  const formatRole = (role) => {
    if (!role) return '';
    return role
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const userRole = user?.role || '';
  const displayRole = formatRole(userRole);
  const alertsRoute = isAdmin ? "/admin/alerts" : "/customer/alerts";
  
  const LogmeOut = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const handleLogoutClick = () => {
    setIsLogoutConfirmOpen(true);
  };
  
  return (
    <header className="app-header w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-12 md:h-14 w-full max-w-full items-center px-3 sm:px-4 md:px-6 safe-x">
        <div className="mr-2 sm:mr-4 flex-shrink-0">
          <SidebarTrigger
            className={`min-h-11 min-w-11 sm:min-h-9 sm:min-w-9 transition-colors ${
              isDarkMode
                ? 'text-white hover:bg-slate-700/50 hover:text-white'
                : 'text-black hover:bg-slate-100 hover:text-black'
            }`}
          />
        </div>

        <nav className="ml-auto flex items-center space-x-1 sm:space-x-2">
            <ThemeToggle 
              variant="ghost" 
              className={`transition-colors ${
                isDarkMode
                  ? 'text-white hover:bg-slate-700/50 hover:text-white'
                  : 'text-black hover:bg-slate-100 hover:text-black'
              }`}
            />
            {isAdmin && (
              <Button
                variant="ghost"
                size="sm"
                title="Wallet"
                onClick={() => navigate('/admin/wallet')}
                className={`transition-colors ${
                  isDarkMode
                    ? 'text-white hover:bg-slate-700/50 hover:text-white'
                    : 'text-black hover:bg-slate-100 hover:text-black'
                }`}
              >
                <Wallet className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="sr-only">Wallet</span>
              </Button>
            )}
             {displayRole && (
              <Badge 
                className="hidden sm:flex text-xs font-medium px-4 py-1 bg-muted text-foreground border-1 border-muted-foreground/20 hover:bg-muted hover:text-foreground"
              >
                {displayRole}
              </Badge>
            )}
            <Button
              variant="ghost"
              size="sm"
              title="Alerts"
              onClick={() => navigate(alertsRoute)}
              className={`relative min-h-11 min-w-11 sm:min-h-0 sm:min-w-0 p-2 sm:p-2 transition-colors ${
                isDarkMode
                  ? 'text-white hover:bg-slate-700/50 hover:text-white'
                  : 'text-black hover:bg-slate-100 hover:text-black'
              }`}
            >
              <Bell className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="absolute -right-0.5 -top-0.5 h-4 min-w-[16px] rounded-full bg-blue-600 px-1 text-[10px] font-bold leading-4 text-white">
                1
              </span>
              <span className="sr-only">Alerts</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogoutClick}
              title="Logout"
              className={`min-h-11 min-w-11 sm:min-h-0 sm:min-w-0 p-2 sm:p-2 transition-colors ${
                isDarkMode
                  ? 'text-white hover:bg-red-900/30 hover:text-red-300'
                  : 'text-black hover:bg-red-50 hover:text-red-600'
              }`}
            >
              <LogOut className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="sr-only">Logout</span>
            </Button>
        </nav>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="absolute top-[calc(3rem+env(safe-area-inset-top,0px))] sm:top-[calc(3.5rem+env(safe-area-inset-top,0px))] left-0 right-0 z-50 sm:hidden safe-x">
            <Card className={`mx-2 sm:mx-4 shadow-lg border-0 ${
              isDarkMode 
                ? 'bg-slate-800/95 backdrop-blur-md border border-slate-700/50' 
                : 'bg-white/95 backdrop-blur-md border border-slate-200/50'
            }`}>
              <CardContent className="p-2">
                <div className="flex flex-col space-y-1">
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`justify-start text-sm transition-colors ${
                        isDarkMode
                          ? 'text-white hover:bg-slate-700/50 hover:text-white'
                          : 'text-black hover:bg-slate-100 hover:text-black'
                      }`}
                      onClick={() => {
                        setIsMenuOpen(false);
                        navigate('/admin/wallet');
                      }}
                    >
                      <Wallet className="h-4 w-4 mr-2" />
                      Wallet
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setIsMenuOpen(false);
                      navigate(alertsRoute);
                    }}
                    className={`justify-start text-sm transition-colors ${
                      isDarkMode
                        ? 'text-white hover:bg-slate-700/50 hover:text-white'
                        : 'text-black hover:bg-slate-100 hover:text-black'
                    }`}
                  >
                    <Bell className="h-4 w-4 mr-2" />
                    Alerts
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setIsMenuOpen(false);
                      handleLogoutClick();
                    }}
                    className={`justify-start text-sm transition-colors ${
                      isDarkMode
                        ? 'text-white hover:bg-red-900/30 hover:text-red-300'
                        : 'text-black hover:bg-red-50 hover:text-red-600'
                    }`}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      {isLogoutConfirmOpen &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
            <Card className="w-full max-w-md md:max-w-lg">
              <CardContent className="p-4 space-y-4">
                <div>
                  <p className="text-base font-semibold text-foreground">Confirm logout</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Are you sure you want to logout?
                  </p>
                </div>
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsLogoutConfirmOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    className="bg-red-600 text-white hover:bg-red-700"
                    onClick={async () => {
                      setIsLogoutConfirmOpen(false);
                      await LogmeOut();
                    }}
                  >
                    Logout
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>,
          document.body
        )}
    </header>
  );
};

export default Header;
